import { Delete, Upload } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, IconButton, Stack, Typography } from '@mui/material';
import Papa from 'papaparse';
import React, { useState } from 'react';
import { useParams } from 'react-router';
import { useGenerateUncategorizedItemsReportMutation, useGetOrganizationQuery } from '../../../../api/organizationSlice';

const SUPPLY_ID_HEADERS = ['supply id', 'supply_id', 'external item id', 'external_item_id', 'item id', 'item_id'];
const CATEGORY_HEADERS = ['current category', 'current_category', 'category'];

const normalizeHeader = (header) => String(header ?? '').trim().toLowerCase();

const validateUncategorizedItemsFile = (file) => new Promise((resolve) => {
    Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        complete: (results) => {
            const rows = results.data ?? [];
            if (rows.length < 2) {
                resolve({
                    isValid: false,
                    error: 'The file must include a header row and at least one item row.'
                });
                return;
            }

            const normalizedHeaders = (rows[0] ?? []).map(normalizeHeader);

            if (normalizedHeaders.length !== 2 || normalizedHeaders.some((header) => header === '')) {
                resolve({
                    isValid: false,
                    error: 'The file must contain exactly two columns.'
                });
                return;
            }

            const hasSupplyIdColumn = normalizedHeaders.some((header) => SUPPLY_ID_HEADERS.includes(header));
            const hasCategoryColumn = normalizedHeaders.some((header) => CATEGORY_HEADERS.includes(header));

            if (!hasSupplyIdColumn || !hasCategoryColumn) {
                resolve({
                    isValid: false,
                    error: 'The header row must include one supply ID (external item ID) column and one current category column.'
                });
                return;
            }

            resolve({ isValid: true });
        },
        error: () => {
            resolve({
                isValid: false,
                error: 'Unable to read this CSV file. Please upload a valid CSV.'
            });
        }
    });
});

function UncategorizedItemsReportUpload() {
    const { organizationId } = useParams();
    const { data: organization } = useGetOrganizationQuery(organizationId);
    const [generateReport, { isLoading }] = useGenerateUncategorizedItemsReportMutation();
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileValidationError, setFileValidationError] = useState(null);

    const clearSelectedFile = () => {
        setSelectedFile(null);
        setFileValidationError(null);
    };

    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            clearSelectedFile();
            return;
        }

        const validation = await validateUncategorizedItemsFile(file);
        setSelectedFile(file);
        setFileValidationError(validation.isValid ? null : validation.error);
        event.target.value = '';
    };

    const handleSubmit = async () => {
        if (!selectedFile || fileValidationError) {
            return;
        }

        const formData = new FormData();
        formData.append('file', selectedFile);
        await generateReport({ data: formData, organizationId }).unwrap();
        clearSelectedFile();
    };

    return (
        <Stack pl={4} spacing={3}>
            <Stack spacing={1}>
                <Typography variant='heavy' sx={{ fontSize: '24px', marginTop: '20px' }}>
                    <strong>Generate Uncategorized Items Report</strong>
                </Typography>
                <Typography>
                    Organization: <strong>{organization?.name}</strong>
                </Typography>
            </Stack>
            <Alert severity='info'>
                Upload a CSV with exactly two columns: one for the supply ID (external item ID) and one for the item&apos;s current category for all items that you want <b>excluded</b> from the final report.
            </Alert>
            {fileValidationError &&
                <Alert severity='error'>
                    {fileValidationError}
                </Alert>
            }
            <Stack spacing={1}>
                { selectedFile ?
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <Typography variant='normal' sx={{ fontSize: '16px' }}>{selectedFile.name}</Typography>
                        <IconButton onClick={clearSelectedFile}>
                            <Delete />
                        </IconButton>
                    </Stack> :
                    <Typography variant='normal' sx={{ fontSize: '16px', color: '#888' }}>
                        No file selected
                    </Typography>
                }
                <Button variant='outlined' disabled={Boolean(selectedFile)} component='label' startIcon={<Upload />} color='primary' style={{ alignSelf: 'start' }}>
                    Upload CSV File
                    <input hidden accept='text/csv' type='file' onChange={handleFileChange} />
                </Button>
                <LoadingButton
                    loading={isLoading}
                    variant='contained'
                    color='primary'
                    style={{ alignSelf: 'start' }}
                    disabled={!selectedFile || Boolean(fileValidationError)}
                    onClick={handleSubmit}
                >
                    Submit Report Request
                </LoadingButton>
            </Stack>
        </Stack>
    );
}

export default UncategorizedItemsReportUpload;
